import React from 'react';
import { useNavigate } from 'react-router-dom';
import StatusBadge from '../../../common/StatusBadge';
import { formatCurrency, formatDate, formatPropertyType } from '../../../../utils/formatters';
import { formatSalesTeam, formatSidAssignment } from '../../../../utils/projectDataUtils';

/**
 * ProjectCard Component
 * Compact mobile-optimized card display for a single project
 * Layout similar to Site Database cards
 */
export default function ProjectCard({ project }) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (project.id) {
      navigate(`/projects/${project.id}`);
    }
  };

  const handleCall = (e) => {
    e.stopPropagation();
    const phone = project.salesForm?.clientPhone;
    if (phone) {
      window.location.href = `tel:${phone.replace(/[^0-9+]/g, '')}`;
    }
  };

  const handleWhatsApp = (e) => {
    e.stopPropagation();
    const phone = project.salesForm?.clientPhone;
    if (phone) {
      const cleanPhone = phone.replace(/[^0-9]/g, '');
      window.open(`https://wa.me/${cleanPhone}`, '_blank');
    }
  };

  const clientPhone = project.salesForm?.clientPhone;
  const propertyType = formatPropertyType(project.salesForm?.propertyType);
  const sidAssignment = formatSidAssignment(project.teamMembers);
  const city = project.salesForm?.city || '';

  // Get short property type (first word only)
  const shortPropertyType = propertyType && propertyType !== 'N/A'
    ? propertyType.split(' - ')[0].split(' ')[0]
    : null;

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-lg border border-gray-200 p-3 cursor-pointer active:bg-gray-50 transition-colors"
    >
      {/* Row 1: Project Number + Property Type */}
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-sm font-semibold text-gray-900">
          {project.projectNumber || 'Pending'}
        </span>
        {shortPropertyType && (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 whitespace-nowrap">
            {shortPropertyType}
          </span>
        )}
      </div>

      {/* Row 2: Status Badge */}
      <div className="mb-2">
        <StatusBadge status={project.workflowStatus} size="sm" />
      </div>

      {/* Row 3: Price + City */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-base font-bold text-gray-900">
          {formatCurrency(project.currentPackagePrice || 0)}
        </span>
        {city && (
          <span className="text-xs text-gray-500">{city}</span>
        )}
      </div>

      {/* Row 4: Sales Team + SID */}
      <div className="grid grid-cols-2 gap-x-2 text-[11px] mb-2">
        <div className="min-w-0">
          <span className="text-gray-400">Sales: </span>
          <span className="font-medium text-gray-700 truncate">
            {formatSalesTeam(project.salesAgents, project.salesForm)}
          </span>
        </div>
        <div className="min-w-0 text-right">
          <span className="text-gray-400">SID: </span>
          <span className="font-medium text-gray-700 truncate">
            {sidAssignment}
          </span>
        </div>
      </div>

      {/* Footer: Date + Quick Actions */}
      <div className="flex justify-between items-center pt-2 border-t border-gray-100">
        <span className="text-[10px] text-gray-500">
          {formatDate(project.createdAt)}
        </span>

        {/* Quick Action Buttons */}
        <div className="flex items-center -mr-1">
          {clientPhone && (
            <>
              <button
                onClick={handleCall}
                className="p-2 text-blue-600 hover:bg-blue-50 active:bg-blue-100 rounded-full transition-colors touch-manipulation"
                aria-label="Call client"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                </svg>
              </button>
              <button
                onClick={handleWhatsApp}
                className="p-2 text-green-600 hover:bg-green-50 active:bg-green-100 rounded-full transition-colors touch-manipulation"
                aria-label="WhatsApp client"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </button>
            </>
          )}
          <button
            onClick={handleClick}
            className="ml-1 px-2 py-1 text-blue-600 text-xs font-medium hover:bg-blue-50 active:bg-blue-100 rounded transition-colors touch-manipulation"
          >
            View
          </button>
        </div>
      </div>
    </div>
  );
}
